import moment from 'moment';
import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { FaRegSave } from 'react-icons/fa';
import { MdAdd, MdArrowBack, MdKeyboardReturn } from 'react-icons/md';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from '../../toasts';
import PropTypes from 'prop-types';
import CustomTypeahead from '../../components/CustomTypeahead';
import FormatedNumberInput from '../../components/FormatedNumberInput';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import { Button, FormField, Section, Select, controlClass } from '../../components/EntityList';
import accountService from '../../services/account.service';
import AssetService from '../../services/asset.service';
import AssetSaleService from '../../services/assetSale.service';
import BankAccountService from '../../services/bankAccount.service';
import ConfigService from '../../services/settings.service';
import UserService from '../../services/user.service';
import { getError } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import { useLocation, useSearchParams } from 'react-router-dom';

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loadingFetch: true };
	case 'FETCH_ASSETS_REQUEST':
	case 'FETCH_ASSET_REQUEST':
		return { ...state, loadingFetch: true };
	case 'FETCH_ASSETS_SUCCESS':
		return { ...state, assets: action.payload, loadingFetch: false };
	case 'FETCH_ASSET_SUCCESS':
		return { ...state, loadingFetch: false };
	case 'FETCH_ASSET_FAIL':
	case 'FETCH_ASSETS_FAIL':
		return { ...state, loadingFetch: false, errorFetch: action.payload };
	case 'FETCH_ACCOUNTS_REQUEST':
		return { ...state, loadingFetch: true };
	case 'FETCH_ACCOUNTS_SUCCESS':
		return { ...state, accounts: action.payload, loadingFetch: false };
	case 'FETCH_ACCOUNTS_FAIL':
		return { ...state, loadingFetch: false, errorFetch: action.payload };
	case 'SAVE_REQUEST':
		return { ...state, saving: true };
	case 'SAVE_SUCCESS':
		return { ...state, saving: false };
	case 'SAVE_FAIL':
		return { ...state, saving: false };
	default:
		return state;
	}
};

const findById = (list, idOrObject) => {
	if (!idOrObject) {
		return null;
	}
	if (typeof idOrObject === 'object') {
		return idOrObject;
	}
	return list.find((item) => item._id === idOrObject) || null;
};

const getBankAccountLabel = (option) => {
	if (!option) {
		return '';
	}
	const owner = option.owner || 'Sin titular';
	const bank = option.bank || 'Sin banco';
	const accountNumber = option.accountNumber || 'Sin numero';
	const currency = option.currency || '';
	return `${owner}: ${bank} - ${accountNumber}${currency ? ` (${currency})` : ''}`;
};

const REQUIRED_FIELD_MESSAGE = 'Este campo es obligatorio.';

const FIELD_LABELS = {
	date: 'Fecha',
	amount: 'Monto',
	asset: 'Activo',
	purchaser: 'Comprador',
	currency: 'Moneda',
	lot: 'Padron',
};

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

const AssetSaleEditor = ({
	mode = 'create',
	assetSaleId = null,
	onSaved,
	initialData = null,
	onCancel,
	preloadAssetId = null,
	saveLabel,
	onReturn,
	returnLabel,
}) => {
	const { t } = useLabels();
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const currencies = ['USD', 'UYU'];
	const isEditing = mode === 'edit';
	const injectedAssetId = searchParams.get('propertyId');
	const injectedAccountId = searchParams.get('accountId');
	const injectedBankAccountId = searchParams.get('bankAccountId');
	const consumedInjectedIds = useRef({
		propertyId: null,
		accountId: null,
		bankAccountId: null,
	});
	// Stable snapshot for the accountId-effect guard below — a round trip's
	// returnTo carries both ids at once when e.g. a purchaser was already
	// selected before creating a bank account, so this must key off the
	// value present at mount, not the (now-mutable) injectedBankAccountId,
	// which the bank-account effect leaves untouched but a manual pick could
	// still change mid-life.
	const injectedBankAccountIdAtMount = useRef(injectedBankAccountId);

	const [resolvedInitialData, setResolvedInitialData] = useState(initialData);
	const [date, setDate] = useState(initialData?.date ? moment.utc(initialData.date) : moment.utc());
	const [amount, setAmount] = useState(initialData?.amount || 0);
	const [purchaser, setPurchaser] = useState();
	const [initialPayment, setInitialPayment] = useState(initialData?.initialPayment || 0);
	const [disabledOwner, setDisabledOwner] = useState(false);
	const [bankAccounts, setBankAccounts] = useState([]);
	const [disabledAsset, setDisabledAsset] = useState(false);
	const [asset, setAsset] = useState();
	const [multipleLots, setMultipleLots] = useState();
	const [lot, setLot] = useState(initialData?.lot || '');
	const [housingUnit, setHousingUnit] = useState(initialData?.housingUnit || '');
	const [paymentTo, setPaymentTo] = useState(initialData?.paymentTo || 'Cliente');
	const [bankAccount, setBankAccount] = useState(null);
	const [currency, setCurrency] = useState(initialData?.currency || '');
	const [companySettings, setCompanySettings] = useState(null);
	const [responsible, setResponsible] = useState(null);
	const [users, setUsers] = useState([]);
	const [fieldErrors, setFieldErrors] = useState({});

	const [{ loadingFetch, saving, accounts, assets, errorFetch }, dispatch] = useReducer(reducer, {
		accounts: [],
		assets: [],
		loadingFetch: true,
		saving: false,
		errorFetch: '',
	});

	useEffect(() => {
		const fetchSale = async () => {
			if (!isEditing || initialData || !assetSaleId) {
				return null;
			}
			const result = await AssetSaleService.getDetails(assetSaleId);
			setResolvedInitialData(result.data);
			setDate(result.data.date ? moment.utc(result.data.date) : moment.utc());
			setAmount(result.data.amount || 0);
			setInitialPayment(result.data.initialPayment || 0);
			setLot(result.data.lot || '');
			setHousingUnit(result.data.housingUnit || '');
			setPaymentTo(result.data.paymentTo || 'Cliente');
			setCurrency(result.data.currency || '');
			return result.data;
		};

		const fetchData = async (fetchedSale) => {
			try {
				dispatch({ type: preloadAssetId ? 'FETCH_ASSET_REQUEST' : 'FETCH_ASSETS_REQUEST' });
				if (preloadAssetId) {
					const result = await AssetService.getDetails(preloadAssetId);
					setAsset(result.data);
					setDisabledAsset(true);
					setMultipleLots(result.data.multipleLots);
					if (result.data.multipleLots === false) {
						setLot(result.data.lot);
					}
					setHousingUnit(result.data.housingUnit || '');
					dispatch({ type: 'FETCH_ASSET_SUCCESS', payload: result.data });
				} else {
					const result = await AssetService.list();
					dispatch({ type: 'FETCH_ASSETS_SUCCESS', payload: result.data });
					const saleAsset = fetchedSale?.asset ?? initialData?.asset;
					if (isEditing && saleAsset) {
						const editingAsset = findById(result.data, saleAsset);
						if (editingAsset) {
							setAsset(editingAsset);
							setDisabledAsset(true);
							setMultipleLots(editingAsset.multipleLots);
						}
					}
				}
			} catch (error) {
				dispatch({ type: preloadAssetId ? 'FETCH_ASSET_FAIL' : 'FETCH_ASSETS_FAIL' });
			}
		};

		const fetchAccountsData = async (fetchedSale) => {
			try {
				dispatch({ type: 'FETCH_ACCOUNTS_REQUEST' });
				const result = await accountService.list();
				dispatch({ type: 'FETCH_ACCOUNTS_SUCCESS', payload: result.data });
				const salePurchaser = fetchedSale?.purchaser ?? initialData?.purchaser;
				if (isEditing && salePurchaser) {
					const editingPurchaser = findById(result.data, salePurchaser);
					if (editingPurchaser) {
						setPurchaser(editingPurchaser);
						setDisabledOwner(true);
					}
				}
			} catch (error) {
				dispatch({ type: 'FETCH_ACCOUNTS_FAIL' });
			}
		};
		const fetchUsers = async (fetchedSale) => {
			try {
				const result = await UserService.list();
				setUsers(result.data);
				const saleResponsible = fetchedSale?.responsible ?? initialData?.responsible;
				if (isEditing && saleResponsible) {
					const editingResponsible = findById(result.data, saleResponsible);
					if (editingResponsible) {
						setResponsible(editingResponsible);
					}
				}
			} catch (error) {
				console.error('Error fetching users:', error);
			}
		};
		const fetchSettings = async () => {
			try {
				const result = await ConfigService.listPublic();
				setCompanySettings(result.data);
			} catch (error) {
				console.error('Error fetching settings:', error);
			}
		};
		const fetchBankAccounts = async (fetchedSale) => {
			try {
				const result = await BankAccountService.list();
				setBankAccounts(result.data);
				const saleBankAccount = fetchedSale?.bankAccount ?? initialData?.bankAccount;
				if (isEditing && saleBankAccount) {
					const editingBankAccount = findById(result.data, saleBankAccount);
					if (editingBankAccount) {
						setBankAccount(editingBankAccount);
					}
				}
			} catch (error) {
				console.error('Error fetching bank accounts:', error);
			}
		};

		fetchSale().then((fetchedSale) => {
			fetchData(fetchedSale);
			fetchAccountsData(fetchedSale);
			fetchUsers(fetchedSale);
			fetchSettings();
			fetchBankAccounts(fetchedSale);
		});
	}, [preloadAssetId, isEditing, assetSaleId]);

	const resolveBankAccount = (account, paymentToValue, currencyValue) => {
		if (!account || !paymentToValue || !currencyValue) {
			return null;
		}
		if (paymentToValue === 'Cliente') {
			if (currencyValue === 'USD') {
				return account.bankAccountUSD || null;
			}
			if (currencyValue === 'UYU') {
				return account.bankAccountUYU || null;
			}
			return null;
		}
		if (paymentToValue === 'Inmobiliaria' && companySettings) {
			if (currencyValue === 'USD') {
				return companySettings.companyBankAccountUSD || null;
			}
			if (currencyValue === 'UYU') {
				return companySettings.companyBankAccountUYU || null;
			}
		}
		return null;
	};

	const updateBankAccount = (account, paymentToValue, currencyValue) => {
		setBankAccount(resolveBankAccount(account, paymentToValue, currencyValue));
	};

	useEffect(() => {
		if (!injectedAssetId || preloadAssetId || !assets.length || consumedInjectedIds.current.propertyId === injectedAssetId) {
			return;
		}
		const injectedAsset = findById(assets, injectedAssetId);
		if (!injectedAsset) {
			return;
		}
		setAsset(injectedAsset);
		setDisabledAsset(true);
		setMultipleLots(injectedAsset.multipleLots);
		if (injectedAsset.multipleLots === false) {
			setLot(injectedAsset.lot);
		}
		setHousingUnit(injectedAsset.housingUnit || '');
		consumedInjectedIds.current.propertyId = injectedAssetId;
	}, [assets, injectedAssetId, preloadAssetId]);

	useEffect(() => {
		if (!injectedAccountId || !accounts.length || consumedInjectedIds.current.accountId === injectedAccountId) {
			return;
		}
		const injectedAccount = findById(accounts, injectedAccountId);
		if (!injectedAccount) {
			return;
		}
		setPurchaser(injectedAccount);
		setDisabledOwner(true);
		setCurrency(injectedAccount.currency || '');
		// Skip deriving the bank account from the purchaser when a bank
		// account was also just injected (see the effect below) — otherwise
		// whichever writer resolves last wins nondeterministically, since
		// accounts and bankAccounts load via separate, independently-timed
		// fetches.
		if (!injectedBankAccountIdAtMount.current) {
			updateBankAccount(injectedAccount, paymentTo, injectedAccount.currency || '');
		}
		consumedInjectedIds.current.accountId = injectedAccountId;
	}, [accounts, injectedAccountId, paymentTo]);

	useEffect(() => {
		if (!injectedBankAccountId || !bankAccounts.length || consumedInjectedIds.current.bankAccountId === injectedBankAccountId) {
			return;
		}
		const injectedBank = findById(bankAccounts, injectedBankAccountId);
		if (!injectedBank) {
			return;
		}
		setBankAccount(injectedBank);
		if (injectedBank.currency && injectedBank.currency !== currency) {
			setCurrency(injectedBank.currency);
		}
		consumedInjectedIds.current.bankAccountId = injectedBankAccountId;
	}, [bankAccounts, currency, injectedBankAccountId]);

	// Keep the URL mirroring the current selection for an injected-id field.
	// Rewrite it on a manual pick (so that choice survives the next round
	// trip) and drop it when the field is cleared — otherwise a stale id
	// from an earlier trip rides along in returnTo and gets re-injected on
	// top of the manual choice.
	const syncInjectedParam = (key, id) => {
		const nextValue = id || null;
		if (searchParams.get(key) === nextValue) return;
		// Pre-mark as consumed so the injection effect doesn't re-apply a
		// record we already hold in state.
		if (nextValue) consumedInjectedIds.current[key] = nextValue;
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			if (nextValue) next.set(key, nextValue);
			else next.delete(key);
			return next;
		}, { replace: true });
	};

	const clearFieldError = (field) => {
		setFieldErrors((prevErrors) => {
			if (!prevErrors[field]) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors[field];
			return nextErrors;
		});
	};

	const validate = () => {
		const errors = {};
		if (!date) errors.date = REQUIRED_FIELD_MESSAGE;
		if (!amount || Number(amount) <= 0) errors.amount = REQUIRED_FIELD_MESSAGE;
		if (!asset) errors.asset = REQUIRED_FIELD_MESSAGE;
		if (!purchaser) errors.purchaser = REQUIRED_FIELD_MESSAGE;
		if (!currency) errors.currency = REQUIRED_FIELD_MESSAGE;
		if (multipleLots && !lot) errors.lot = REQUIRED_FIELD_MESSAGE;
		return errors;
	};

	const handleAccountChange = (selected) => {
		if (!selected || selected.length === 0) {
			setPurchaser(null);
			setCurrency('');
			setBankAccount(null);
			syncInjectedParam('accountId', null);
			clearFieldError('purchaser');
			clearFieldError('currency');
		} else {
			const selectedAccount = selected[0];
			setPurchaser(selectedAccount);
			setCurrency(selectedAccount.currency);
			updateBankAccount(selectedAccount, paymentTo, selectedAccount.currency);
			syncInjectedParam('accountId', selectedAccount._id);
			clearFieldError('purchaser');
			clearFieldError('currency');
		}
	};

	const handlePaymentToChange = (event) => {
		const newPaymentTo = event.target.value;
		setPaymentTo(newPaymentTo);
		if (purchaser && currency) {
			updateBankAccount(purchaser, newPaymentTo, currency);
		}
	};

	const handleCurrencyChange = (event) => {
		const newCurrency = event.target.value;
		setCurrency(newCurrency);
		if (purchaser && paymentTo) {
			updateBankAccount(purchaser, paymentTo, newCurrency);
		}
		clearFieldError('currency');
	};

	const handleSave = async () => {
		const errors = validate();
		if (Object.keys(errors).length > 0) {
			setFieldErrors(errors);
			const missingLabels = Object.keys(errors).map((field) => FIELD_LABELS[field] || field);
			toast.error(`Complete el/los siguiente/s campo/s requerido/s: ${missingLabels.join(', ')}.`);
			return;
		}
		try {
			dispatch({ type: 'SAVE_REQUEST' });
			const payload = {
				amount,
				asset,
				currency,
				purchaser,
				initialPayment,
				lot,
				housingUnit,
				date,
				paymentTo,
				bankAccount: bankAccount?._id || bankAccount,
				responsible: responsible?._id || responsible,
			};
			const savedId = assetSaleId || resolvedInitialData?._id;
			let resultId = savedId;
			if (isEditing && savedId) {
				await AssetSaleService.edit(savedId, payload);
				toast.success(t('msg.assetSaleUpdated'));
			} else {
				const result = await AssetSaleService.create(payload);
				resultId = result.data?.assetSale?._id || result.data?._id || savedId;
				toast.success(t('msg.assetSaleCreated'));
			}
			dispatch({ type: 'SAVE_SUCCESS' });
			setFieldErrors({});
			if (onSaved) {
				onSaved(resultId);
			}
		} catch (ex) {
			dispatch({ type: 'SAVE_FAIL' });
			toast.error(getError(ex));
		}
	};

	const handleAssetChange = (selected) => {
		const selectedAsset = selected[0];
		setAsset(selectedAsset || null);
		syncInjectedParam('propertyId', selectedAsset?._id);
		clearFieldError('asset');
		if (selected[0]) {
			setMultipleLots(selected[0].multipleLots);
			if (!selected[0].multipleLots) {
				setLot(selected[0].lot);
			}
			setHousingUnit(selected[0].housingUnit || '');
			clearFieldError('lot');
		} else {
			setMultipleLots(false);
			setLot('');
			setHousingUnit('');
		}
	};

	const bankAccountsForCurrency = useMemo(
		() => (currency ? bankAccounts.filter((ba) => ba.currency === currency) : bankAccounts),
		[bankAccounts, currency],
	);
	const currencyOptions = useMemo(() => currencies.map((currencyOption) => ({ value: currencyOption, label: currencyOption })), []);
	const paymentToOptions = useMemo(
		() => [
			{ value: 'Cliente', label: t('enum.paymentTo.cliente') },
			{ value: 'Inmobiliaria', label: t('enum.paymentTo.inmobiliaria') },
		],
		[t],
	);

	if (loadingFetch) {
		return <DetailPageLoader entityLabel={t('entity.assetSale')} />;
	}

	if (errorFetch) {
		return (
			<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
				{errorFetch}
			</div>
		);
	}

	const resolvedSaveLabel = saveLabel || (isEditing ? t('action.save') : t('action.add'));

	return (
		<Form id='asset-sale-editor-form' onSubmit={(event) => {
			event.preventDefault();
			handleSave();
		}} className='tw:space-y-4'>
			<div className='tw:sticky tw:top-14 tw:z-20 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface/95 tw:px-5 tw:py-3.5 tw:shadow-sm tw:backdrop-blur'>
				<div className='tw:text-[12.5px] tw:text-ink-2'>
					{isEditing ? 'Actualizá los datos de la venta.' : 'Completá los datos para registrar la venta.'}
				</div>
				<div className='tw:flex tw:gap-2'>
					<Button icon={<MdArrowBack />} onClick={onCancel} disabled={saving}>
						{t('action.cancel')}
					</Button>
					{onReturn && (
						<Button icon={<MdKeyboardReturn />} onClick={onReturn} disabled={saving}>
							{returnLabel || t('action.back')}
						</Button>
					)}
					<Button
						variant='primary'
						icon={isEditing ? <FaRegSave /> : <MdAdd />}
						type='submit'
						disabled={saving || loadingFetch}
					>
						{saving ? 'Guardando...' : resolvedSaveLabel}
					</Button>
				</div>
			</div>

			<Section title='Datos de la venta'>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='asset-sale-date' label={FIELD_LABELS.date} required>
						<DatePicker
							id='asset-sale-date'
							selected={date ? moment(date).toDate() : null}
							onChange={(value) => {
								setDate(value);
								clearFieldError('date');
							}}
							dateFormat='dd-MM-yyyy'
							className={controlClass}
							placeholderText='dd-mm-yyyy'
							disabled={saving}
						/>
						<FieldError message={fieldErrors.date} />
					</FormField>
					<FormField id='asset-sale-amount' label={FIELD_LABELS.amount} required>
						<FormatedNumberInput
							value={amount}
							className={controlClass}
							onChange={(event) => {
								setAmount(event.target.value);
								clearFieldError('amount');
							}}
							min={0}
							disabled={saving}
						/>
						<FieldError message={fieldErrors.amount} />
					</FormField>
					{!preloadAssetId && (
						<FormField id='asset-sale-asset' label={FIELD_LABELS.asset} required>
							<CustomTypeahead
								id='assets'
								labelKey={(option) => `${option.name}`}
								onChange={handleAssetChange}
								options={assets}
								placeholder={t('form.placeholder.searchAssetSpace')}
								selected={asset ? [asset] : null}
								disabled={disabledAsset || saving}
								isInvalid={!!fieldErrors.asset}
								linkToCreate={`/AdminScreen/newAsset?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
								newOption='nuevo Activo Fijo'
								variant='entity'
							/>
							<FieldError message={fieldErrors.asset} />
						</FormField>
					)}
					{preloadAssetId && (
						<FormField id='asset-sale-asset-readonly' label={FIELD_LABELS.asset} required>
							<Form.Control id='asset-sale-asset-readonly' className={controlClass} readOnly value={asset ? asset.name : ''} />
						</FormField>
					)}
					<FormField id='asset-sale-lot' label={FIELD_LABELS.lot}>
						<Form.Control
							id='asset-sale-lot'
							className={controlClass}
							value={lot || ''}
							disabled={!multipleLots || saving}
							onChange={(event) => {
								setLot(event.target.value);
								clearFieldError('lot');
							}}
						/>
						<FieldError message={fieldErrors.lot} />
					</FormField>
					<FormField id='asset-sale-housing-unit' label={t('field.housingUnit')}>
						<Form.Control
							id='asset-sale-housing-unit'
							className={controlClass}
							value={housingUnit || ''}
							disabled={saving}
							onChange={(event) => setHousingUnit(event.target.value)}
						/>
					</FormField>
				</div>
			</Section>

			<Section title='Comprador y pago'>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='asset-sale-purchaser' label={FIELD_LABELS.purchaser} required>
						<CustomTypeahead
							id='accounts'
							labelKey={(option) => `${option.name}`}
							onChange={handleAccountChange}
							options={accounts}
							disabled={disabledOwner || saving}
							selected={purchaser ? [purchaser] : null}
							placeholder={t('form.placeholder.searchAccount')}
							isInvalid={!!fieldErrors.purchaser}
							linkToCreate={`/AdminScreen/newAccount?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
							newOption='nueva cuenta'
							variant='entity'
						/>
						<FieldError message={fieldErrors.purchaser} />
					</FormField>
					<FormField id='asset-sale-currency' label={FIELD_LABELS.currency} required>
						<Select
							id='asset-sale-currency'
							options={currencyOptions}
							value={currency || ''}
							disabled={saving}
							onChange={handleCurrencyChange}
							placeholder='Elegí Moneda'
						/>
						<FieldError message={fieldErrors.currency} />
					</FormField>
					<FormField id='asset-sale-initial-payment' label={t('field.initialDelivery')}>
						<FormatedNumberInput
							value={initialPayment}
							className={controlClass}
							onChange={(event) => setInitialPayment(event.target.value)}
							min={0}
							max={amount}
							disabled={saving}
						/>
					</FormField>
					<FormField id='asset-sale-payment-to' label={t('form.paymentTo')}>
						<Select
							id='asset-sale-payment-to'
							options={paymentToOptions}
							value={paymentTo}
							disabled={saving}
							onChange={handlePaymentToChange}
						/>
					</FormField>
					{paymentTo === 'Cliente' ? (
						<FormField id='asset-sale-bank-account' label={t('form.paymentAccount')}>
							<CustomTypeahead
								id='asset-sale-bank-account'
								labelKey={getBankAccountLabel}
								onChange={(selected) => {
									const nextBankAccount = selected && selected.length > 0 ? selected[0] : null;
									setBankAccount(nextBankAccount);
									syncInjectedParam('bankAccountId', nextBankAccount?._id);
								}}
								options={bankAccountsForCurrency}
								selected={bankAccount ? [bankAccount] : []}
								placeholder='Buscar cuenta bancaria...'
								linkToCreate={`/AdminScreen/newBankAccount?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
								newOption='nueva cuenta bancaria'
								disabled={saving}
								variant='entity'
							/>
						</FormField>
					) : (
						bankAccount && (
							<FormField id='asset-sale-bank-account-readonly' label={t('form.paymentAccount')}>
								<Form.Control
									id='asset-sale-bank-account-readonly'
									className={controlClass}
									readOnly
									value={`${bankAccount.bank} - ${bankAccount.accountNumber} (${bankAccount.currency})`}
								/>
							</FormField>
						)
					)}
				</div>
			</Section>

			<Section title='Responsable'>
				<FormField id='asset-sale-responsible' label={t('field.responsible')}>
					<CustomTypeahead
						id='users'
						labelKey={(option) => `${option.name}`}
						onChange={(selected) => setResponsible(selected && selected.length > 0 ? selected[0] : null)}
						options={users}
						placeholder={t('form.placeholder.searchResponsibleUser')}
						selected={responsible ? [responsible] : null}
						disabled={saving}
						variant='entity'
					/>
				</FormField>
			</Section>
		</Form>
	);
};

AssetSaleEditor.propTypes = {
	mode: PropTypes.oneOf(['create', 'edit']),
	assetSaleId: PropTypes.string,
	onSaved: PropTypes.func,
	initialData: PropTypes.object,
	onCancel: PropTypes.func.isRequired,
	preloadAssetId: PropTypes.string,
	saveLabel: PropTypes.string,
	onReturn: PropTypes.func,
	returnLabel: PropTypes.string,
};

export default AssetSaleEditor;
